/**
 * Pure route-topology analysis (M68).
 *
 * Measurement only — no balance decisions. Shared cells between routes are
 * first-class: two MapRoute.cells lists may contain the same grid positions.
 */

import { footprintCenter } from '../model/towers'
import { normalizeMapRoutes, routeTrack } from '../model/path'
import type { GridPosition, MapDefinition } from '../model/types'
import { SMALL_TOWER_RANGE, validAnchors2x2 } from './laneMetrics'

const FOOTPRINT_2X2 = { width: 2, height: 2 } as const

function cellKey(c: GridPosition): string {
  return `${c.x},${c.y}`
}

function withinRange(center: { x: number; y: number }, cell: GridPosition, range: number): boolean {
  const dx = center.x - (cell.x + 0.5)
  const dy = center.y - (cell.y + 0.5)
  return Math.hypot(dx, dy) <= range
}

/** Unique ground-route cells (terrain union keys). */
export function groundRouteCellUnion(map: MapDefinition): GridPosition[] {
  const seen = new Set<string>()
  const out: GridPosition[] = []
  for (const route of normalizeMapRoutes(map)) {
    if (route.movementClass !== 'GROUND') continue
    for (const cell of route.cells) {
      const key = cellKey(cell)
      if (seen.has(key)) continue
      seen.add(key)
      out.push({ ...cell })
    }
  }
  return out
}

/**
 * Cells that appear on two or more ground routes (topological share).
 * Visual-only crossings without shared cells are not counted.
 */
export function sharedGroundCells(map: MapDefinition): GridPosition[] {
  const counts = new Map<string, { cell: GridPosition; n: number }>()
  for (const route of normalizeMapRoutes(map)) {
    if (route.movementClass !== 'GROUND') continue
    const seenOnRoute = new Set<string>()
    for (const cell of route.cells) {
      const key = cellKey(cell)
      if (seenOnRoute.has(key)) continue
      seenOnRoute.add(key)
      const entry = counts.get(key)
      if (entry) entry.n += 1
      else counts.set(key, { cell: { ...cell }, n: 1 })
    }
  }
  return [...counts.values()].filter((e) => e.n >= 2).map((e) => e.cell)
}

export interface AnchorCoverageDetail {
  anchor: GridPosition
  /** Ground route ids whose cells intersect the tower range. */
  groundRouteIds: string[]
  /** Air route ids in range. */
  airRouteIds: string[]
  /** Distinct ground cells covered (union). */
  groundCellsCovered: number
  /** Shared ground cells covered. */
  sharedCellsCovered: number
  /** Sum of per-route covered-cell counts (shared cells counted per route). */
  weightedRouteCells: number
  /** Fraction of each ground route's cells covered, keyed by route id. */
  routeCoverageFraction: Record<string, number>
}

export interface RouteCoverageSummary {
  routeId: string
  movementClass: 'GROUND' | 'AIR'
  cells: number
  length: number
  cellsCovered: number
  coverageFraction: number
}

/**
 * Coverage of one candidate 2×2 anchor against all map routes.
 */
export function analyzeAnchorCoverage(
  map: MapDefinition,
  anchor: GridPosition,
  range = SMALL_TOWER_RANGE
): AnchorCoverageDetail {
  const routes = normalizeMapRoutes(map)
  const shared = new Set(sharedGroundCells(map).map(cellKey))
  const center = footprintCenter(anchor, FOOTPRINT_2X2)
  const groundRouteIds: string[] = []
  const airRouteIds: string[] = []
  const coveredGround = new Set<string>()
  let sharedCellsCovered = 0
  let weightedRouteCells = 0
  const routeCoverageFraction: Record<string, number> = {}

  for (const route of routes) {
    const hit = route.cells.filter((cell) => withinRange(center, cell, range))
    routeCoverageFraction[route.id] = route.cells.length === 0 ? 0 : hit.length / route.cells.length
    if (hit.length === 0) continue
    if (route.movementClass === 'AIR') {
      airRouteIds.push(route.id)
      continue
    }
    groundRouteIds.push(route.id)
    weightedRouteCells += hit.length
    for (const cell of hit) {
      const key = cellKey(cell)
      if (!coveredGround.has(key)) {
        coveredGround.add(key)
        if (shared.has(key)) sharedCellsCovered += 1
      }
    }
  }

  return {
    anchor: { ...anchor },
    groundRouteIds,
    airRouteIds,
    groundCellsCovered: coveredGround.size,
    sharedCellsCovered,
    weightedRouteCells,
    routeCoverageFraction
  }
}

/**
 * Top N legal 2×2 anchors ranked by weighted multi-route coverage, then shared cells.
 */
export function rankAnchorsByRouteCoverage(
  map: MapDefinition,
  range = SMALL_TOWER_RANGE,
  limit = 10
): AnchorCoverageDetail[] {
  const anchors = validAnchors2x2(map)
  const ranked = anchors
    .map((anchor) => analyzeAnchorCoverage(map, anchor, range))
    .sort((a, b) => {
      const routesA = a.groundRouteIds.length
      const routesB = b.groundRouteIds.length
      if (routesB !== routesA) return routesB - routesA
      if (b.sharedCellsCovered !== a.sharedCellsCovered) return b.sharedCellsCovered - a.sharedCellsCovered
      if (b.weightedRouteCells !== a.weightedRouteCells) return b.weightedRouteCells - a.weightedRouteCells
      if (b.groundCellsCovered !== a.groundCellsCovered) return b.groundCellsCovered - a.groundCellsCovered
      if (a.anchor.y !== b.anchor.y) return a.anchor.y - b.anchor.y
      return a.anchor.x - b.anchor.x
    })
  return ranked.slice(0, limit)
}

export function summarizeRouteCoverage(
  map: MapDefinition,
  anchor: GridPosition,
  range = SMALL_TOWER_RANGE
): RouteCoverageSummary[] {
  const detail = analyzeAnchorCoverage(map, anchor, range)
  return normalizeMapRoutes(map).map((route) => {
    const fraction = detail.routeCoverageFraction[route.id] ?? 0
    return {
      routeId: route.id,
      movementClass: route.movementClass,
      cells: route.cells.length,
      length: routeTrack(route).totalLength,
      cellsCovered: Math.round(fraction * route.cells.length),
      coverageFraction: fraction
    }
  })
}

export interface TopologySnapshot {
  groundRoutes: number
  airRoutes: number
  unionGroundCells: number
  sharedGroundCells: number
  pathTilesOnGrid: number
  /** True when every painted P belongs to the ground-route union (terrain = union). */
  terrainMatchesUnion: boolean
  topAnchors: AnchorCoverageDetail[]
}

export function snapshotTopology(map: MapDefinition, range = SMALL_TOWER_RANGE): TopologySnapshot {
  const routes = normalizeMapRoutes(map)
  const union = groundRouteCellUnion(map)
  const shared = sharedGroundCells(map)
  let pathTilesOnGrid = 0
  for (let y = 0; y < map.height; y += 1) {
    for (let x = 0; x < map.width; x += 1) {
      if (map.rows[y]![x] === 'P') pathTilesOnGrid += 1
    }
  }
  const unionKeys = new Set(union.map(cellKey))
  let orphanP = 0
  for (let y = 0; y < map.height; y += 1) {
    for (let x = 0; x < map.width; x += 1) {
      if (map.rows[y]![x] === 'P' && !unionKeys.has(`${x},${y}`)) orphanP += 1
    }
  }
  return {
    groundRoutes: routes.filter((r) => r.movementClass === 'GROUND').length,
    airRoutes: routes.filter((r) => r.movementClass === 'AIR').length,
    unionGroundCells: union.length,
    sharedGroundCells: shared.length,
    pathTilesOnGrid,
    terrainMatchesUnion: orphanP === 0 && pathTilesOnGrid === union.length,
    topAnchors: rankAnchorsByRouteCoverage(map, range, 10)
  }
}

export interface AnchorBucketCounts {
  anchors1Route: number
  anchors2Route: number
  anchors3Route: number
  anchors4PlusRoute: number
  total: number
}

/** Count legal 2×2 anchors by how many ground routes they cover at `range`. */
export function countAnchorsByGroundRoutes(
  map: MapDefinition,
  range = SMALL_TOWER_RANGE
): AnchorBucketCounts {
  const anchors = validAnchors2x2(map)
  const counts: AnchorBucketCounts = {
    anchors1Route: 0,
    anchors2Route: 0,
    anchors3Route: 0,
    anchors4PlusRoute: 0,
    total: anchors.length
  }
  for (const anchor of anchors) {
    const n = analyzeAnchorCoverage(map, anchor, range).groundRouteIds.length
    if (n <= 0) continue
    if (n === 1) counts.anchors1Route += 1
    else if (n === 2) counts.anchors2Route += 1
    else if (n === 3) counts.anchors3Route += 1
    else counts.anchors4PlusRoute += 1
  }
  return counts
}

export interface DominantAnchorReport {
  /** Top-ranked anchor by multi-route / weighted coverage. */
  top: AnchorCoverageDetail | null
  /** Second-ranked, if any. */
  second: AnchorCoverageDetail | null
  /**
   * Ratio of top.weightedRouteCells / second.weightedRouteCells.
   * Null when fewer than two anchors exist.
   */
  weightedDominanceRatio: number | null
  /**
   * Heuristic flag — surfaces evidence only; does not auto-reject.
   * True when the top anchor covers ≥2 ground routes AND weighted coverage is
   * ≥1.75× the runner-up (or the map has multi-route anchors and top covers all).
   */
  flagged: boolean
  reason: string
}

/**
 * Detect whether one placement looks dramatically superior.
 * Premium seams are good; a single universal "always build here" is usually bad.
 */
export function detectDominantAnchor(
  map: MapDefinition,
  range = SMALL_TOWER_RANGE
): DominantAnchorReport {
  const ranked = rankAnchorsByRouteCoverage(map, range, 25)
  const top = ranked[0] ?? null
  const second = ranked[1] ?? null
  if (!top) {
    return {
      top: null,
      second: null,
      weightedDominanceRatio: null,
      flagged: false,
      reason: 'no legal anchors'
    }
  }
  if (!second) {
    return {
      top,
      second: null,
      weightedDominanceRatio: null,
      flagged: false,
      reason: 'only one ranked anchor'
    }
  }
  const ratio =
    second.weightedRouteCells > 0
      ? top.weightedRouteCells / second.weightedRouteCells
      : top.weightedRouteCells > 0
        ? Infinity
        : 1
  const groundRoutes = normalizeMapRoutes(map).filter((r) => r.movementClass === 'GROUND').length
  const coversAll = groundRoutes >= 2 && top.groundRouteIds.length >= groundRoutes
  const multiPremium = top.groundRouteIds.length >= 2 && ratio >= 1.75
  const flagged = coversAll || multiPremium
  let reason = 'spread coverage healthy'
  if (coversAll && groundRoutes >= 3) {
    reason = `top anchor covers all ${groundRoutes} ground routes`
  } else if (coversAll) {
    reason = `top anchor covers both ground routes (ratio ${ratio.toFixed(2)})`
  } else if (multiPremium) {
    reason = `top multi-route weighted coverage ${ratio.toFixed(2)}× runner-up`
  }
  return { top, second, weightedDominanceRatio: ratio, flagged, reason }
}
